import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Star, Quote, Phone, MessageCircle } from 'lucide-react';
import { testimonials, advisorInfo } from '../data/mock';

const TestimonialsSection = () => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        className={`w-5 h-5 ${index < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-yellow-100 text-yellow-800 text-sm font-medium px-4 py-2 rounded-full mb-4">
            <Star className="w-4 h-4 mr-2" />
            Customer Reviews
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Apa Kata 
            <span className="text-blue-600"> Pelanggan Kami</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Dengar pengalaman sebenar daripada pelanggan yang telah berjaya mendapatkan kereta impian mereka
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg bg-white rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                {/* Quote Icon */}
                <div className="flex justify-between items-start mb-6">
                  <Quote className="w-8 h-8 text-blue-200" />
                  <div className="flex space-x-1">
                    {renderStars(testimonial.rating)}
                  </div>
                </div>

                {/* Testimonial Text */}
                <blockquote className="text-slate-700 mb-6 italic leading-relaxed">
                  "{testimonial.comment}"
                </blockquote>

                {/* Customer Info */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {testimonial.model}
                      </p>
                    </div>
                  </div>

                  {/* Verified Badge */}
                  <div className="mt-4">
                    <Badge className="bg-green-100 text-green-800 text-xs px-3 py-1">
                      ✓ Verified Customer
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overall Rating Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center mb-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-6xl font-bold text-blue-600 mb-4">5.0</div>
            <div className="flex justify-center space-x-1 mb-4">
              {renderStars(5)}
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Rating Keseluruhan Pelanggan
            </h3>
            <p className="text-slate-600 mb-6">
              Berdasarkan 500+ review pelanggan yang berpuas hati
            </p>
            
            {/* Rating Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 mb-2">98%</div>
                <div className="text-sm text-slate-600">Recommend Syasyah</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-2">99%</div>
                <div className="text-sm text-slate-600">Satisfied Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 mb-2">24/7</div>
                <div className="text-sm text-slate-600">Support Response</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-2xl p-8 text-white max-w-4xl mx-auto">
            <div className="text-5xl mb-6">💬</div>
            <h3 className="text-3xl font-bold mb-4">
              Berminat Nak Dapatkan Kereta Impian?
            </h3>
            <p className="text-green-100 mb-8 text-lg max-w-2xl mx-auto">
              Join ribuan pelanggan lain yang telah berjaya mendapatkan kereta dengan mudah. 
              Dapatkan kiraan loan atau brochure digital sekarang!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya berminat nak dapatkan kereta impian saya. Boleh start proses?')}`, '_blank')}
                className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Phone className="w-5 h-5 mr-3" />
                WhatsApp Syasyah
              </Button>
              <Button 
                onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, boleh hantar brochure digital semua model Perodua 2025?')}`, '_blank')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-green-600 font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5 mr-3" />
                Minta Brochure
              </Button>
            </div>

            {/* Quick Contact Info */}
            <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{advisorInfo.phone}</span>
                </div>
                <div className="hidden sm:block w-1 h-1 bg-white rounded-full"></div>
                <div className="flex items-center space-x-2">
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;